import type { GridRow, GridRowsPage } from '../contracts/public-api';
import { asGridSourceRow } from './grid-source-row.interface';
import { resolveExistingBucketKey } from './grid-orchestration-bucket-path';
import { GROUP_PATH_DELIMITER } from './grid-orchestration.constants';
import type { GroupPaginationState } from './grid-orchestration.types';
import { GROUP_TREE_KEY_DELIMITER } from './grid-orchestration.utils';

/** Merged row list produced from per-bucket pagination state. */
export interface MergedPaginationElements {
  /** Deduplicated nodes from every bucket. */
  readonly nodes: Array<GridRow>;
  /** Sum of primary-bucket total counts. */
  readonly totalCount: number;
  /** True when any bucket still has a next page. */
  readonly hasMore: boolean;
}

/** Immutably updates a single bucket entry in a pagination map. */
export function immutableUpdateGroupPaginationState(
  current: Map<string, GroupPaginationState>,
  groupValue: string,
  updated: GroupPaginationState
): Map<string, GroupPaginationState> {
  const next: Map<string, GroupPaginationState> = new Map<string, GroupPaginationState>(current);
  next.set(groupValue, updated);
  return next;
}

/** Sums totalCount from primary bucket keys only (excludes nested composite paths). */
export function sumPrimaryBucketTotals(pagination: Map<string, GroupPaginationState>): number {
  let totalCount: number = 0;
  for (const [key, state] of pagination.entries()) {
    if (!key.includes(GROUP_PATH_DELIMITER)) {
      totalCount += state.totalCount;
    }
  }
  return totalCount;
}

/** Merges all per-group pagination nodes into a single elements response shape. */
export function mergeGroupPaginationIntoElementsData(
  pagination: Map<string, GroupPaginationState>
): MergedPaginationElements {
  const allNodes: Array<GridRow> = new Array<GridRow>();
  const seenIds: Set<string> = new Set<string>();

  for (const state of pagination.values()) {
    for (const node of state.nodes) {
      const id = asGridSourceRow(node).id;
      if (id === undefined || seenIds.has(id)) {
        continue;
      }
      seenIds.add(id);
      allNodes.push(node);
    }
  }

  const totalCount: number = sumPrimaryBucketTotals(pagination);
  const hasMore: boolean = Array.from(pagination.values()).some(
    (state: GroupPaginationState): boolean => state.hasNextPage
  );

  return { nodes: allNodes, totalCount, hasMore };
}

/** Returns whether any bucket still has server pages to fetch. */
export function computeGlobalHasMoreFromPagination(pagination: Map<string, GroupPaginationState>): boolean {
  return Array.from(pagination.values()).some((state: GroupPaginationState): boolean => state.hasNextPage);
}

/**
 * Resolves the server-reported total for a bucket path. Primary-level keys
 * are seeded in pagination; nested composite paths only appear in sub-group counts.
 */
export function resolveExpectedBucketTotal(
  path: string,
  paginationTotal: number,
  subGroupCounts: Record<string, number>
): number {
  if (path.includes(GROUP_PATH_DELIMITER)) {
    const countKey: string = path.split(GROUP_PATH_DELIMITER).join(GROUP_TREE_KEY_DELIMITER);
    const subCount: number | undefined = subGroupCounts[countKey];
    if (subCount !== undefined && subCount > 0) {
      return subCount;
    }
  }
  return paginationTotal;
}

/**
 * Records a per-bucket server total as a sub-group count, or returns null when nothing changes.
 *
 * Nested group headers read their total from `subGroupCounts` and fall back to loaded rows when
 * no entry exists, so a bucket the summary tree never covered displays the page size until every
 * page has streamed in. The bucket's own total is known on the first page and fixes the header
 * immediately.
 *
 * Summary-tree entries are authoritative and are never overwritten: they are scoped to the whole
 * query, whereas a bucket total is only trustworthy for the bucket that was actually requested.
 * "Already recorded" is decided by {@link resolveExistingBucketKey} rather than an exact string
 * match, because the tree keys buckets by backend slug while this path may carry the grid's
 * spelling of the same bucket. A second entry for one bucket is not merely redundant:
 * `resolveSubGroupCount` sums every slug sharing a display label, so it silently doubles the
 * header total.
 *
 * @param path - Bucket path, delimited by {@link GROUP_PATH_DELIMITER}.
 * @param fetchedTotal - Server total reported for this bucket.
 * @param current - Existing sub-group counts.
 * @returns Updated counts, or null when the path is not nested, the total is unusable, or this
 *   bucket is already recorded under any spelling.
 */
export function recordBucketTotalAsSubGroupCount(
  path: string,
  fetchedTotal: number,
  current: Record<string, number>
): Record<string, number> | null {
  if (!path.includes(GROUP_PATH_DELIMITER) || fetchedTotal <= 0) {
    return null;
  }

  const countKey: string = path.split(GROUP_PATH_DELIMITER).join(GROUP_TREE_KEY_DELIMITER);
  if (resolveExistingBucketKey(countKey, Object.keys(current)) !== null) {
    return null;
  }

  return { ...current, [countKey]: fetchedTotal };
}

/** Creates a default skeleton pagination entry for a path not yet in the map. */
export function createBucketSkeletonState(path: string, totalCount: number): GroupPaginationState {
  return {
    nodes: [],
    endCursor: null,
    hasNextPage: true,
    totalCount,
    loading: false,
    pagesLoaded: 0
  };
}

/** Returns true when a queued bucket path should be fetched. */
export function isBucketLoadable(state: GroupPaginationState | undefined): boolean {
  if (state === undefined) {
    return true;
  }
  if (!state.hasNextPage || state.loading) {
    return false;
  }
  if (state.pagesLoaded > 0 && state.endCursor === null && state.cursorUsableForElements !== false) {
    return false;
  }
  return true;
}

/**
 * Returns true when the batch first-page loader should fetch this bucket.
 *
 * Cursor pagination loads later pages separately. Re-queuing a loaded bucket
 * through the batch API would refetch page one forever while `hasNextPage`
 * stays true.
 */
export function isBucketFirstPagePending(state: GroupPaginationState | undefined): boolean {
  if (state === undefined) {
    return true;
  }
  return state.pagesLoaded === 0 && !state.loading && state.hasNextPage;
}

/**
 * Computes the next pagination state after applying a fetched page.
 *
 * The displayed total and the pagination stop condition are resolved
 * independently. A summary count outranks the fetched `totalCount` for display
 * because a bucket fetch can report an unscoped count, but it must not end
 * paging: an undercounting summary would strand rows the server still offers.
 *
 * @param path - Bucket path being paged.
 * @param state - Current state for the bucket.
 * @param response - Page just fetched for this bucket.
 * @param resolvedTotal - Server summary total for the bucket, or 0 when unknown.
 * @param options - Set `clientNullFilter` when rows are filtered client-side.
 *   Set `cursorSource` to `'batch'` when the page came from `elementsForGroupBuckets`.
 * @returns Next immutable bucket state.
 */
export function computeNextBucketStateAfterFetch(
  path: string,
  state: GroupPaginationState,
  response: GridRowsPage,
  resolvedTotal: number,
  options?: { clientNullFilter?: boolean; cursorSource?: 'batch' | 'elements' }
): GroupPaginationState {
  const fetchedTotal: number = response.totalCount ?? 0;
  const useClientNullFilter: boolean = options?.clientNullFilter === true;
  const serverHasNextPage: boolean = response.pageInfo?.hasNextPage ?? false;
  const emptyFirstPage: boolean =
    !useClientNullFilter && state.pagesLoaded === 0 && response.nodes.length === 0 && fetchedTotal === 0;
  const preserveSummaryCount: boolean = emptyFirstPage && resolvedTotal > 0;
  const nextNodes: Array<GridRow> =
    state.pagesLoaded === 0 ? [...response.nodes] : appendUniqueBucketNodes(state.nodes, response.nodes);
  const cursorUsableForElements: boolean = options?.cursorSource !== 'batch';

  let totalCount: number;
  if (useClientNullFilter) {
    totalCount = Math.max(resolvedTotal, fetchedTotal);
  } else if (resolvedTotal > 0 || fetchedTotal === 0) {
    totalCount = resolvedTotal;
  } else {
    totalCount = fetchedTotal;
  }

  let hasNextPage: boolean;
  if (useClientNullFilter) {
    // Rows are discarded after the fetch, so the cursor cannot signal
    // completion on its own and the expected count has to bound the scan.
    hasNextPage = nextNodes.length < totalCount && serverHasNextPage;
  } else if (preserveSummaryCount) {
    hasNextPage = false;
  } else {
    hasNextPage = serverHasNextPage;
  }

  return {
    nodes: nextNodes,
    endCursor: response.pageInfo?.endCursor ?? null,
    hasNextPage,
    totalCount,
    loading: false,
    pagesLoaded: state.pagesLoaded + 1,
    cursorUsableForElements,
    ...(useClientNullFilter
      ? {
          useClientNullFilter: true,
          nullScanCursor: response.pageInfo?.endCursor ?? null
        }
      : {})
  };
}

/**
 * Appends incoming nodes, skipping ids already present in the bucket.
 * Used when an `elements` continuation overlaps the batch first page.
 */
function appendUniqueBucketNodes(
  existing: ReadonlyArray<GridRow>,
  incoming: ReadonlyArray<GridRow>
): Array<GridRow> {
  const seenIds: Set<string> = new Set<string>();
  const merged: Array<GridRow> = [];
  for (const node of existing) {
    const id: string | undefined = asGridSourceRow(node).id;
    if (id !== undefined) {
      seenIds.add(id);
    }
    merged.push(node);
  }
  for (const node of incoming) {
    const id: string | undefined = asGridSourceRow(node).id;
    if (id !== undefined && seenIds.has(id)) {
      continue;
    }
    if (id !== undefined) {
      seenIds.add(id);
    }
    merged.push(node);
  }
  return merged;
}
